import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { DsButtonModule, DsMessageModule } from '@bmw-ds/components';
import { SearchResultMode } from 'customer-data-frontend/src/form/components/search-results/model/search-result-mode.model';

import { FoundCustomer } from '../../../customer/model/found-customer.model';
import { ValidSearchResultsComponent } from '../../../form/components/search-results/components/valid-search-results/valid-search-results.component';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ButtonRowComponent } from '../../../modal/button/button-row.component';
import { VerifyChoiceModalWrapper } from '../../../modal/verify-choice-modal/verify-choice-modal.wrapper';

@Component({
  selector: 'cdf-customer-duplicate-warning',
  imports: [DsMessageModule, DsButtonModule, ValidSearchResultsComponent, VerifyChoiceModalWrapper, ButtonRowComponent],
  host: {
    class: 'd-flex flex-column gap-6x',
  },
  template: `
    <span class="title-md">{{ i18n('customerCreation.duplicateHeading') }} </span>
    <ds-message [messageText]="i18n('customerCreation.duplicateWarning')" tone="caution" />

    <cdf-valid-search-results
      [searchResults]="duplicates()"
      [loadingCustomer]="loadingCustomer()"
      [searchResultMode]="searchResultMode"
      (openCustomerProfile)="openDuplicateProfile.emit($event)"
    />

    <p class="mx-2">{{ i18n('customerCreation.duplicateInstructions', { customers: duplicates().length }) }}</p>

    <cdf-button-row>
      <ds-button variant="outline" [disabled]="saving()" (click)="backFromDuplicate.emit()">{{
        i18n('customerCreation.backFromDuplicateButton')
      }}</ds-button>
      <ds-button
        id="ignore-button"
        variant="outline"
        ds-loading
        [disabled]="saving()"
        [isLoading]="saving()"
        (click)="checkAndIgnoreDuplicate()"
        >{{ i18n('customerCreation.ignoreDuplicateButton') }}</ds-button
      >
    </cdf-button-row>
    <cdf-verify-choice-modal-wrapper
      [(open)]="createCustomerInRetailOnlyModalOpen"
      [headingText]="i18n('customerCreation.ignoreGcdmDuplicateModalHeading')"
      [bodyText]="i18n('customerCreation.ignoreGcdmDuplicateModalBody')"
      [verifyButtonText]="i18n('customerCreation.ignoreGcdmDuplicateModalAgree')"
      [buttonLoading]="saving()"
      (verify)="ignoreDuplicateOnlyInRetail()"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerDuplicateWarningComponent {
  protected readonly i18n = useI18n();

  readonly duplicates = input.required<FoundCustomer[]>();
  readonly saving = input<boolean>(false);
  readonly loadingCustomer = input<FoundCustomer>();

  readonly backFromDuplicate = output();

  protected readonly createCustomerInRetailOnlyModalOpen = signal(false);
  protected readonly searchResultMode = SearchResultMode.DUPLICATE_CHECK;

  /**
   * Ignores duplicates and initiates creating new customer.
   * The boolean describes if the creation is allowed to be forwarded to the NSC Database or not.
   */
  readonly ignoreDuplicates = output<boolean>();
  readonly openDuplicateProfile = output<FoundCustomer>();

  ignoreDuplicateOnlyInRetail() {
    // Only create customer in Retail CRM
    this.ignoreDuplicates.emit(false);
    this.createCustomerInRetailOnlyModalOpen.set(false);
  }

  checkAndIgnoreDuplicate() {
    const duplicateExistsInNscDatabase = this.duplicates().find((dup) => dup.gcid);

    // If a customer with GCID was found, a creation in GCDM is not possible, as no duplicates regarding E-Mail are allowed within GCDM
    if (duplicateExistsInNscDatabase) {
      this.createCustomerInRetailOnlyModalOpen.set(true);
      return;
    }

    // Allowed to create customer in GCDM and Retail CRM
    this.ignoreDuplicates.emit(true);
  }
}
